/**
 * Monotonic reconciliation for the SST V2 door-state caches (LOCKER-321).
 *
 * The V2 dashboard reconciles each door from TWO caches that are written
 * by TWO async writers (MQTT `SstReservationUpdate` and polled
 * `getThings`/`updateThings`/`ThingUpdated`) in arbitrary order:
 *
 *   CACHE-L — `locker.sst2Locker.doors[].reservations[]` (the array the
 *             door tile reads for OCCUPIED vs AVAILABLE).
 *   CACHE-R — each SL2 Reservation thing's embedded `sstReservation`
 *             snapshot (the dashboard's Direction-2 desync cross-check).
 *
 * The bug class: a write that is OLDER than what the client already knows
 * reverts a door's state. The only monotonic fact both writers and both
 * caches share is that an SST reservation's status is a one-way street
 * toward a TERMINAL state (CLOSED/CANCELLED/DELETED and their V1-shortened
 * CLOSE/CANCEL forms). Once the client has observed a reservation reach a
 * terminal status it must NEVER be resurrected as active — regardless of a
 * stale locker snapshot re-adding it (lodash index-merge could not delete
 * array elements) or a stale reservation snapshot reverting its status.
 *
 * This module is the single, principled expression of that invariant. It
 * generalizes the #292 point-fix (which protected only CACHE-R's status
 * field) into a rule applied uniformly to every door-state write path:
 *
 *   - a per-session TERMINAL TOMBSTONE set of reservation ids, and
 *   - a wholesale door-array REPLACE (never index-merge) that filters
 *     tombstoned / self-terminal reservations out on ingest.
 */
import { SST2_TERMINAL_STATUSES } from "./sst2Status"

/** True when an SST reservation status marks a completed workflow. */
export function isSst2TerminalStatus(status: unknown): boolean {
  return (
    typeof status === "string" &&
    (SST2_TERMINAL_STATUSES as readonly string[]).includes(status)
  )
}

/**
 * Extract the SST reservation id from either shape:
 *   - V2 locker-embedded reservation → top-level `id`.
 *   - SL2 Reservation thing's embedded snapshot → `_id` (SL2 convention),
 *     occasionally normalized to top-level `id`.
 */
export function getSst2ReservationId(res: any): string | undefined {
  const id = res?.id ?? res?._id ?? res?.reservation_id
  return typeof id === "string" ? id : undefined
}

/**
 * Collect the ids of every reservation already TERMINAL inside a locker
 * snapshot's doors. Keeps the tombstone set current from polled/pushed
 * snapshots (not only from MQTT), so an even-staler later snapshot cannot
 * revive a reservation this one already reported closed.
 */
export function collectTerminalReservationIds(sst2Locker: any): string[] {
  const ids: string[] = []
  const doors = sst2Locker?.doors
  if (!Array.isArray(doors)) return ids
  for (const door of doors) {
    const reservations = door?.reservations
    if (!Array.isArray(reservations)) continue
    for (const res of reservations) {
      if (isSst2TerminalStatus(res?.status)) {
        const id = getSst2ReservationId(res)
        if (id) ids.push(id)
      }
    }
  }
  return ids
}

/**
 * Wholesale-replacement reconcile of an incoming locker door snapshot
 * (CACHE-L). Returns a NEW doors array — callers assign it in place of the
 * incoming array, NEVER lodash-merge it — in which any reservation the
 * client already knows is terminal (tombstoned) or that is itself terminal
 * in the snapshot has been dropped.
 *
 * Invariant: a terminal reservation can never ghost back onto a door via a
 * stale/out-of-order locker snapshot (the LOCKER-321 race). Non-terminal
 * reservations pass through untouched so legitimate lifecycle transitions
 * (e.g. DELIVERED → EXPIRED) and genuine out-of-band SST reservations
 * (Direction-1) still render.
 */
export function reconcileLockerDoors(
  incomingDoors: any,
  terminalReservationIds: Record<string, true>,
): any {
  if (!Array.isArray(incomingDoors)) return incomingDoors
  return incomingDoors.map((door) => {
    const reservations = door?.reservations
    if (!Array.isArray(reservations)) return door
    const filtered = reservations.filter((res: any) => {
      const id = getSst2ReservationId(res)
      if (id && terminalReservationIds[id]) return false // tombstoned
      if (isSst2TerminalStatus(res?.status)) return false // self-terminal
      return true
    })
    if (filtered.length === reservations.length) return door
    return { ...door, reservations: filtered }
  })
}

/**
 * CACHE-R terminal-stickiness test: should we KEEP the existing (terminal)
 * `sstReservation` snapshot rather than accept an incoming one that would
 * revert it to a visible/non-terminal status?
 *
 * True when the client already knows the reservation is terminal (its
 * existing snapshot is terminal OR its id is tombstoned) AND the incoming
 * snapshot is non-terminal. This is the CACHE-R mirror of
 * `reconcileLockerDoors` and the generalization of the #292 guard.
 */
export function shouldKeepTerminalReservation(
  existingSst: any,
  incomingSst: any,
  terminalReservationIds: Record<string, true>,
): boolean {
  if (!existingSst || !incomingSst) return false
  const resId =
    getSst2ReservationId(existingSst) ?? getSst2ReservationId(incomingSst)
  const knownTerminal =
    isSst2TerminalStatus(existingSst.status) ||
    (resId ? !!terminalReservationIds[resId] : false)
  const incomingTerminal = isSst2TerminalStatus(incomingSst.status)
  return knownTerminal && !incomingTerminal
}
